import { FC, PropsWithChildren, useEffect, useRef } from 'react';

import { useBoothInfo } from '@auth-v2';
import { useAtomValue, useSetAtom } from 'jotai';

import { useActivity } from '@queries';

import { LiveAnalysisListener } from './LiveAnalysisListener';
import { MessageListener } from './MessageListener';
import { dispatchLiveFeedbackAtom, liveFeedbackActiveAtom } from './state/atoms';

type FloorConnectionProps = {
    currentActivityId?: number;
    onActivityIdChange: (activityId: number | null) => void;
};

const useBoothConnection = (currentActivityId?: number) => {
    const { data: booth } = useBoothInfo();
    const messageEndpoint = booth?.beastEndpoint;
    const { data: currentActivity } = useActivity(currentActivityId ?? -1);

    const isLiveAnalysisActive = useAtomValue(liveFeedbackActiveAtom);

    const dispatch = useSetAtom(dispatchLiveFeedbackAtom);

    const currentActivityIdRef = useRef(currentActivityId);
    useEffect(() => {
        if (currentActivityIdRef.current !== currentActivityId) {
            dispatch(null);
            currentActivityIdRef.current = currentActivityId;
        }
    }, [currentActivityId, dispatch]);

    useEffect(() => {
        if (!isLiveAnalysisActive) dispatch(null);
    }, [dispatch, isLiveAnalysisActive]);

    return { currentActivity, messageEndpoint, isLiveAnalysisActive };
};

export const KioskConnection: FC<PropsWithChildren<{ currentActivityId: number }>> = ({
    children,
    currentActivityId,
}) => {
    const { currentActivity, messageEndpoint, isLiveAnalysisActive } = useBoothConnection(currentActivityId);

    return (
        <>
            {currentActivity && messageEndpoint && (
                <MessageListener
                    currentActivity={currentActivity}
                    messageEndpoint={messageEndpoint}
                    device="kiosk"
                    onActivityIdChange={() => {}}
                />
            )}
            {currentActivity && messageEndpoint && isLiveAnalysisActive && <LiveAnalysisListener />}
            {children}
        </>
    );
};

export const FloorConnection: FC<PropsWithChildren<FloorConnectionProps>> = ({
    children,
    currentActivityId,
    onActivityIdChange,
}) => {
    const { currentActivity, messageEndpoint, isLiveAnalysisActive } = useBoothConnection(currentActivityId);

    return (
        <>
            {messageEndpoint && (
                <MessageListener
                    currentActivity={currentActivity}
                    messageEndpoint={messageEndpoint}
                    device="floor"
                    onActivityIdChange={onActivityIdChange}
                />
            )}
            {currentActivity && messageEndpoint && isLiveAnalysisActive && <LiveAnalysisListener />}
            {children}
        </>
    );
};
